import { ChangeDetectionStrategy, Component, effect, signal, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SystemActionComponent } from "./abstract.system.action.component";
import { Item } from "../../../pojo/Item";
import { WorkflowCustomAttributeMappingsComponent } from "./workflow.custom.attribute.mappings.component";
import {
    parseWorkflowCustAtrbMappings,
    serializeWorkflowCustAtrbMappings,
    WorkflowCustAtrbMappingUiRow,
} from "./workflow.cust.atrb.mappings.util";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-add-org-custom-attributes-action",
    template: `
        <div class="form-group">
            <small class="form-text">
                Custom attribute values are automatically collected from all ORG_CUSTOM_ATTRIBUTE form fields in the
                submission. You can also map additional custom attributes manually below.
            </small>
            <label class="checkbox-option">
                <input
                    type="checkbox"
                    [checked]="useManualMappings()"
                    (change)="onToggleManualMappings($any($event.target).checked)"
                />
                Map custom attributes manually
            </label>
        </div>

        @if (useManualMappings()) {
            <s25-ng-workflow-custom-attribute-mappings
                [itemTypeId]="itemIds.Organization"
                [mappings]="mappings()"
                (mappingsChange)="onMappingsChange($event)"
            />
        }
    `,
    styles: `
        .form-group {
            margin-bottom: 16px;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 8px;
            display: block;
        }

        .checkbox-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            font-weight: normal;
        }

        .checkbox-option input[type="checkbox"] {
            margin: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, WorkflowCustomAttributeMappingsComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddOrgCustomAttributesActionComponent extends SystemActionComponent {
    protected readonly actionCode = "ADD_ORG_CUSTOM_ATTRIBUTES" as const;
    readonly itemIds = Item.Ids;
    readonly useManualMappings = signal(false);
    readonly mappings = signal<WorkflowCustAtrbMappingUiRow[]>([]);
    private initialized = false;

    constructor() {
        super();

        effect(() => {
            const inputs = this.inputMap();
            if (this.initialized) return;
            this.initialized = true;
            this.initializeFromInputs(inputs);
        });
    }

    private initializeFromInputs(inputs: Record<string, { inputValue?: string } | undefined>) {
        const parsed = parseWorkflowCustAtrbMappings(inputs["orgCustomAttributes"]?.inputValue);
        if (parsed.length) {
            this.useManualMappings.set(true);
            this.mappings.set(parsed);
        }
    }

    onToggleManualMappings(checked: boolean) {
        this.useManualMappings.set(checked);
        if (!checked) {
            this.mappings.set([]);
            this.setInputValue("orgCustomAttributes", "");
        }
    }

    onMappingsChange(rows: WorkflowCustAtrbMappingUiRow[]): void {
        this.mappings.set(rows);
        this.setInputValue("orgCustomAttributes", serializeWorkflowCustAtrbMappings(rows));
    }
}
